import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Database, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Play,
  ArrowRight,
  Hash,
  Link2,
  BookOpen,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';

// P0 FIX: Use same-origin API calls to avoid CORS issues in production
const API = ''; // Relative URL - browser uses same origin

const CanonicalIdMigrationDashboard = ({ getAuthHeaders }) => {
  const [status, setStatus] = useState(null);
  const [courseStatus, setCourseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState({});
  const [recentAliases, setRecentAliases] = useState({});
  const [activeTab, setActiveTab] = useState('canonical');

  const entityLabels = {
    courses: 'Courses (Bundles)',
    mock_exams: 'Mock Exams',
    coupons: 'Coupons',
    source_registry: 'Sources',
    exam_profiles: 'Exam Profiles'
  };

  const entityOrder = ['mock_exams', 'coupons', 'source_registry', 'exam_profiles', 'courses'];

  const fetchStatus = useCallback(async () => {
    try {
      // Fetch canonical ID status
      const canonicalRes = await fetch(`${API}/api/admin/canonical-ids/status`, {
        headers: getAuthHeaders()
      });
      if (canonicalRes.ok) {
        const data = await canonicalRes.json();
        setStatus(data.status);
      }
      
      // Fetch course_id status
      const courseRes = await fetch(`${API}/api/admin/course-migration/status`, {
        headers: getAuthHeaders()
      });
      if (courseRes.ok) {
        const data = await courseRes.json();
        setCourseStatus(data.status);
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
      toast.error('Fehler beim Laden des Status');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleMigrate = async (entityType, dryRun = false) => {
    setMigrating(prev => ({ ...prev, [entityType]: true }));
    
    try {
      const response = await fetch(
        `${API}/api/admin/canonical-ids/migrate/${entityType}?dry_run=${dryRun}`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (dryRun) {
          toast.info(`Dry Run: ${data.updated} Entities würden migriert werden`);
        } else {
          toast.success(`${data.updated} Entities migriert, ${data.aliases_created} Aliases erstellt`);
          fetchStatus();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Migration fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Migration fehlgeschlagen: ' + error.message);
    } finally {
      setMigrating(prev => ({ ...prev, [entityType]: false }));
    }
  };

  const handleMigrateAll = async () => {
    setMigrating(prev => ({ ...prev, all: true }));
    
    try {
      const response = await fetch(
        `${API}/api/admin/canonical-ids/migrate-all?dry_run=false`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Migration abgeschlossen: ${data.summary.total_updated} Entities, ${data.summary.total_aliases_created} Aliases`);
        fetchStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Migration fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Migration fehlgeschlagen: ' + error.message);
    } finally {
      setMigrating(prev => ({ ...prev, all: false }));
    }
  };

  const fetchAliases = async (entityType) => {
    try {
      const response = await fetch(
        `${API}/api/admin/canonical-ids/aliases/${entityType}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRecentAliases(prev => ({ ...prev, [entityType]: data.aliases }));
      }
    } catch (error) {
      console.error('Failed to fetch aliases:', error);
    }
  };

  const handleCourseMigration = async () => {
    setMigrating(prev => ({ ...prev, courses: true }));
    
    try {
      const response = await fetch(
        `${API}/api/admin/course-migration/run`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const steps = data.results?.steps || {};
        const total = Object.values(steps).reduce((acc, s) => acc + (s.updated || 0), 0);
        toast.success(`Course Migration: ${total} Einträge aktualisiert`);
        fetchStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Migration fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Migration fehlgeschlagen: ' + error.message);
    } finally {
      setMigrating(prev => ({ ...prev, courses: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const allComplete = status && Object.values(status).every(
    s => s.total === 0 || s.completion_percent === 100
  );

  const courseComplete = courseStatus && 
    courseStatus.courses?.with_exam_profile_mapping === courseStatus.courses?.total &&
    courseStatus.mock_exams?.with_course_id === courseStatus.mock_exams?.total &&
    courseStatus.coupons?.with_course_id === courseStatus.coupons?.total;

  return (
    <div className="space-y-6" data-testid="canonical-id-migration-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            ID Migration & Course Architecture
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Einheitliche IDs und Course-Zuordnung für alle Entities
          </p>
        </div>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchStatus}
          data-testid="refresh-status-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Aktualisieren
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="canonical" className="flex items-center gap-2">
            <Hash className="w-4 h-4" />
            Canonical IDs
          </TabsTrigger>
          <TabsTrigger value="courses" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Course Architecture
          </TabsTrigger>
        </TabsList>

        {/* Canonical ID Tab */}
        <TabsContent value="canonical" className="space-y-4 mt-4">
          {/* Overall Status */}
          <div className="flex items-center justify-between">
            {allComplete ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 flex-1">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Canonical IDs vollständig!</p>
                  <p className="text-sm text-green-600">Alle Entities haben Canonical IDs</p>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={handleMigrateAll}
                disabled={migrating.all}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="migrate-all-btn"
              >
                {migrating.all ? (
                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-1" />
                )}
                Alle migrieren
              </Button>
            )}
          </div>

          {/* Entity Cards */}
          <div className="grid gap-4">
            {entityOrder.map(entityType => {
          const entityStatus = status?.[entityType] || {
            total: 0,
            with_canonical_id: 0,
            without_canonical_id: 0,
            completion_percent: 0,
            alias_count: 0
          };
          
          const isComplete = entityStatus.total === 0 || entityStatus.completion_percent === 100;
          const isPrimary = entityType === 'mock_exams';
          
          return (
            <Card 
              key={entityType} 
              className={`${isPrimary ? 'border-blue-200 bg-blue-50/30' : ''} ${isComplete ? 'border-green-200' : 'border-orange-200'}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">
                      {entityLabels[entityType]}
                    </CardTitle>
                    {isPrimary && (
                      <Badge variant="default" className="bg-blue-600">Primär</Badge>
                    )}
                    {isComplete ? (
                      <Badge variant="outline" className="text-green-600 border-green-300">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        100%
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-orange-600 border-orange-300">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {entityStatus.completion_percent}%
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fetchAliases(entityType)}
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      Aliases ({entityStatus.alias_count})
                    </Button>
                    
                    {!isComplete && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleMigrate(entityType, false)}
                        disabled={migrating[entityType]}
                        data-testid={`migrate-${entityType}-btn`}
                      >
                        {migrating[entityType] ? (
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <ArrowRight className="w-4 h-4 mr-1" />
                        )}
                        Migrieren
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Progress Bar */}
                <div className="mb-3">
                  <Progress 
                    value={entityStatus.completion_percent} 
                    className="h-2"
                  />
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div className="text-center p-2 bg-white rounded border">
                    <div className="text-lg font-bold">{entityStatus.total}</div>
                    <div className="text-xs text-slate-500">Gesamt</div>
                  </div>
                  <div className="text-center p-2 bg-white rounded border">
                    <div className="text-lg font-bold text-green-600">{entityStatus.with_canonical_id}</div>
                    <div className="text-xs text-slate-500">Mit canonical_id</div>
                  </div>
                  <div className="text-center p-2 bg-white rounded border">
                    <div className="text-lg font-bold text-orange-600">{entityStatus.without_canonical_id}</div>
                    <div className="text-xs text-slate-500">Ohne</div>
                  </div>
                  <div className="text-center p-2 bg-white rounded border">
                    <div className="text-lg font-bold text-blue-600">{entityStatus.alias_count}</div>
                    <div className="text-xs text-slate-500">Aliases</div>
                  </div>
                </div>
                
                {/* Aliases Display */}
                {recentAliases[entityType] && recentAliases[entityType].length > 0 && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      Alias Mappings (legacy → canonical)
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {recentAliases[entityType].slice(0, 10).map((alias, idx) => (
                        <div key={idx} className="text-xs font-mono flex items-center gap-2">
                          <span className="text-slate-500 truncate max-w-[150px]">{alias.legacy_id}</span>
                          <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <span className="text-blue-600 truncate">{alias.canonical_id}</span>
                        </div>
                      ))}
                      {recentAliases[entityType].length > 10 && (
                        <p className="text-xs text-slate-400">... und {recentAliases[entityType].length - 10} weitere</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
          </div>
        </TabsContent>

        {/* Course Architecture Tab */}
        <TabsContent value="courses" className="space-y-4 mt-4">
          {/* Course Status */}
          <div className="flex items-center justify-between">
            {courseComplete ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 flex-1">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Course Architecture vollständig!</p>
                  <p className="text-sm text-green-600">Alle Entities haben course_id Zuordnung</p>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                onClick={handleCourseMigration}
                disabled={migrating.courses}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="course-migration-btn"
              >
                {migrating.courses ? (
                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Layers className="w-4 h-4 mr-1" />
                )}
                Course Migration ausführen
              </Button>
            )}
          </div>

          {/* Course List */}
          {courseStatus?.courses?.list && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                  Registrierte Kurse ({courseStatus.courses.total})
                </CardTitle>
                <CardDescription>
                  Kurse mit exam_profile Mapping: {courseStatus.courses.with_exam_profile_mapping}/{courseStatus.courses.total}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  {courseStatus.courses.list.map((course, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${
                        course.default_exam_profile_id ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">{course.code}</span>
                          <span className="text-xs text-slate-500 ml-2 font-mono">{course.canonical_id}</span>
                        </div>
                        {course.default_exam_profile_id ? (
                          <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                            {course.default_exam_profile_id}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                            Kein exam_profile
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Entity course_id Status */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className={courseStatus?.mock_exams?.with_course_id === courseStatus?.mock_exams?.total ? 'border-green-200' : 'border-orange-200'}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Mock Exams</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {courseStatus?.mock_exams?.with_course_id || 0}/{courseStatus?.mock_exams?.total || 0}
                </div>
                <p className="text-xs text-slate-500">mit course_id</p>
              </CardContent>
            </Card>
            
            <Card className={courseStatus?.coupons?.with_course_id === courseStatus?.coupons?.total ? 'border-green-200' : 'border-orange-200'}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Coupons</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {courseStatus?.coupons?.with_course_id || 0}/{courseStatus?.coupons?.total || 0}
                </div>
                <p className="text-xs text-slate-500">mit course_id</p>
              </CardContent>
            </Card>
            
            <Card className={courseStatus?.questions?.with_course_id === courseStatus?.questions?.total ? 'border-green-200' : 'border-orange-200'}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Questions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {courseStatus?.questions?.with_course_id || 0}/{courseStatus?.questions?.total || 0}
                </div>
                <p className="text-xs text-slate-500">mit course_id</p>
              </CardContent>
            </Card>
          </div>

          {/* Info Box */}
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <CardDescription className="text-sm">
                <p className="font-medium text-purple-800 mb-2">Course Architecture</p>
                <ul className="list-disc list-inside space-y-1 text-purple-700">
                  <li><strong>Course</strong> = Zentrale Produkt-Entität (Katalog/Filter)</li>
                  <li><strong>mock_exams.course_id</strong> → verknüpft mit Course</li>
                  <li><strong>coupons.course_id</strong> → verknüpft mit Course</li>
                  <li><strong>questions.course_id</strong> → verknüpft mit Course</li>
                  <li><strong>AI Engine</strong> bleibt bei exam_profile_id (Blueprint)</li>
                  <li>Courses haben <code>default_exam_profile_id</code> für AI Engine Verknüpfung</li>
                </ul>
              </CardDescription>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Legacy Info Box */}
      <Card className="bg-slate-50">
        <CardContent className="p-4">
          <CardDescription className="text-sm">
            <p className="font-medium text-slate-700 mb-2">ID-System Übersicht</p>
            <ul className="list-disc list-inside space-y-1 text-slate-600">
              <li><strong>Canonical IDs</strong>: Eindeutige IDs für alle Entities (mock_*, course_*, etc.)</li>
              <li><strong>Course Architecture</strong>: Course als zentrale Produkt-Entität</li>
              <li><strong>AI Engine</strong>: Bleibt bei exam_profile_id für Versionierung</li>
              <li>Legacy IDs bleiben für READ via Alias-Lookup unterstützt</li>
              <li>CANONICAL_STRICT_MODE blockiert neue Writes mit Legacy IDs</li>
            </ul>
          </CardDescription>
        </CardContent>
      </Card>
    </div>
  );
};

export default CanonicalIdMigrationDashboard;
